// src/context/MuiThemeProvider.jsx
import React, { useEffect, useMemo, useState } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { useTheme } from "./ThemeContext";

const readVar = (name, fallback) => {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name);
  return (v && v.trim()) || fallback;
};

export const MuiThemeProvider = ({ children }) => {
  const { theme } = useTheme(); // 'dark' | 'light'
  const mode = theme === "dark" ? "dark" : "light";

  // Bump a version AFTER the DOM applies the .dark class so CSS vars are updated
  const [cssVarsVersion, setCssVarsVersion] = useState(0);
  useEffect(() => {
    // wait one frame for classList changes -> styles apply -> computed styles valid
    const id = requestAnimationFrame(() => setCssVarsVersion(v => v + 1));
    return () => cancelAnimationFrame(id);
  }, [mode]);

  const muiTheme = useMemo(() => {
    // Read from CSS variables (single source of truth)
    const bg          = readVar("--ui-bg",           mode === "dark" ? "#121212" : "#f8f8f8");
    const card        = readVar("--ui-card",         mode === "dark" ? "#1a1a1a" : "#ffffff");
    const hover       = readVar("--ui-hover",        mode === "dark" ? "#222222" : "#f0f0f0");
    const border      = readVar("--ui-border",       mode === "dark" ? "#2a2a2a" : "#dcdcdc");
    const borderHover = readVar("--ui-border-hover", mode === "dark" ? "#3a3a3a" : "#c7c7c7");
    const detail      = readVar("--ui-detail",       "#EE6c1b");

    const textPrimary   = readVar("--ui-text-primary",   mode === "dark" ? "#f5f5f5" : "#1a1a1a");
    const textSecondary = readVar("--ui-text-secondary", mode === "dark" ? "#e0e0e0" : "#3a3a3a");
    const textMuted     = readVar("--ui-text-muted",     mode === "dark" ? "#b3b3b3" : "#9e9e9e");

    return createTheme({
      palette: {
        mode,
        primary:   { main: detail, contrastText: "#fff" },
        background:{ default: bg, paper: card },
        divider:   border,
        text:      { primary: textPrimary, secondary: textSecondary, disabled: textMuted },
      },
      typography: {
        fontFamily: `'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif`,
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 700,
      },
      shape: { borderRadius: 12 },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            ":root": { colorScheme: mode },
            body: {
              backgroundColor: bg,
              color: textPrimary,
              transition: "background-color .2s, color .2s",
            },
            // Keep <strong>/<b> actually bold everywhere
            ".MuiTypography-root strong, .MuiTypography-root b, strong, b": {
              fontWeight: 700,
            },
            // Code font (optional variable)
            "code, kbd, samp, pre": {
              fontFamily:
                'var(--font-code, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace) !important',
              fontFeatureSettings: '"calt" 1, "liga" 1',
              fontVariantLigatures: "contextual common-ligatures discretionary-ligatures",
              textRendering: "optimizeLegibility",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
              tabSize: 2,
            },
          },
        },
        // Force high-contrast for most variants; keep caption/overline/subtitle2 a bit softer
        MuiTypography: {
          styleOverrides: {
            root: ({ ownerState, theme }) => {
              const v = ownerState?.variant;
              const soft = new Set(["caption", "overline", "subtitle2"]);
              return {
                color: soft.has(v) ? theme.palette.text.secondary : theme.palette.text.primary,
              };
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundColor: card,
              color: textPrimary,
              borderColor: border,
              transition: "background-color .2s, color .2s, border-color .2s",
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: "none",
              "&:hover": { backgroundColor: hover },
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            notchedOutline: { borderColor: border },
            root: {
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: borderHover },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: detail },
            },
            input: { color: textPrimary },
          },
        },
      },
    });
  // include cssVarsVersion so we re-read CSS vars AFTER the toggle applies
  }, [mode, cssVarsVersion]);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};